import type {FileMetadata, MetadataNode} from '@wandb/weave/core/model/types';
import {makeZip} from 'client-zip';

// Note: Our version of Typescript does not think createWritable exists on FileSystemFileHandle
// so we need to augment the global type for typechecking until it is updated
declare global {
  interface FileSystemFileHandle {
    createWritable(): Promise<WritableStream>;
  }
}

export type ArtifactFileEntry = {
  path: string;
  url: string;
  size: number;
  ref?: string;
};

export const ARTIFACT_DOWNLOAD_SIZE_LIMIT = 500 * 1000 * 1000; // 500 MB
export const ARTIFACT_DOWNLOAD_FILE_COUNT_LIMIT = 1000;

export const OPFS_AVAILABLE =
  typeof navigator?.storage?.getDirectory === 'function' &&
  'FileSystemWritableFileStream' in globalThis;

export async function removeOpfsFile(filename: string): Promise<void> {
  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(filename);
  } catch {
    // File may not exist or OPFS may not be supported, ignore either way
  }
}

const PREFETCH_CONCURRENCY = 4;

type ZipEntry = {input: ReadableStream; name: string; size: number};

function fetchEntry(
  entry: ArtifactFileEntry,
  onProgress: ((bytes: number) => void) | undefined
): Promise<ZipEntry> {
  // url is already prefixed with the backend host when the file tree is built
  // oxlint-disable-next-line wandb/no-unprefixed-urls
  return fetch(`${entry.url}?stream=true`, {
    credentials: 'include',
  }).then(res => {
    if (!res.ok) {
      throw new Error(`Failed to fetch ${entry.path}: ${res.status}`);
    }
    if (res.body == null) {
      throw new Error(`No response body for ${entry.path}`);
    }
    // Track progress on the raw fetch bytes so that downloadedBytes matches artifactSize
    const progressStream = new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        controller.enqueue(chunk);
        onProgress?.(chunk.byteLength);
      },
    });
    const body = res.body.pipeThrough(progressStream);
    return {input: body, name: entry.path, size: entry.size};
  });
}

// Streams artifact files into a zip stored in the OPFS.
// Prefetches up to PREFETCH_CONCURRENCY files ahead so that connection setup
// for the next batch overlaps with zip-writing of the current file.
export async function buildZipAndStoreInOpfs(
  entries: ArtifactFileEntry[],
  zipFilename: string,
  onProgress?: (bytesWritten: number) => void,
  signal?: AbortSignal
): Promise<FileSystemFileHandle> {
  const root = await navigator.storage.getDirectory();
  const fileHandle = await root.getFileHandle(zipFilename, {create: true});
  const writable = await fileHandle.createWritable();

  async function* fetchEntries() {
    const buffer: Promise<ZipEntry>[] = [];
    let i = 0;

    while (i < PREFETCH_CONCURRENCY && i < entries.length) {
      buffer.push(fetchEntry(entries[i++], onProgress));
    }

    while (buffer.length > 0) {
      const result = await buffer.shift()!;
      if (i < entries.length) {
        buffer.push(fetchEntry(entries[i++], onProgress));
      }
      yield result;
    }
  }

  const zipStream = makeZip(fetchEntries(), {buffersAreUTF8: true});
  await zipStream.pipeTo(writable, {signal});
  return fileHandle;
}

// Triggers a browser download of a file stored in OPFS via a temporary blob URL
export async function downloadOpfsFile(
  opfsFileHandle: FileSystemFileHandle,
  filename: string
): Promise<void> {
  const file = await opfsFileHandle.getFile();
  const url = URL.createObjectURL(file);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Recursively flattens a MetadataNode tree into a flat list of file entries
export function flattenFilesMetadata(tree: MetadataNode): ArtifactFileEntry[] {
  if (tree.type === 'file') {
    return [
      {path: tree.fullPath, url: tree.url, size: tree.size, ref: tree.ref},
    ];
  }
  const files = Object.values(tree.files).map((f: FileMetadata) => ({
    path: f.fullPath,
    url: f.url,
    size: f.size,
    ref: f.ref,
  }));
  return [...files, ...Object.values(tree.dirs).flatMap(flattenFilesMetadata)];
}

export function formatEta(remainingMs: number): string {
  const rounded = Math.ceil(remainingMs / 5000) * 5;
  if (rounded < 60) {
    return `~${rounded}s`;
  }
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  return seconds > 0 ? `~${minutes}m ${seconds}s` : `~${minutes}m`;
}
